// Just like a useState, but accepts an optional delay parameter in the set function. Calling set with no delay will cancel any pending delayed state updates.
import { useState, useRef, useEffect } from 'react';

export function useDelayedState<T>(
  initialValue: T
): [T, (value: T, delay?: number) => void] {
  const [state, setState] = useState(initialValue);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const setDelayedState = (value: T, delay?: number) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (delay !== undefined) {
      timeoutRef.current = setTimeout(() => {
        setState(value);
        timeoutRef.current = null;
      }, delay);
    } else {
      setState(value);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return [state, setDelayedState];
}
